import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, Plus, Save, Search, Sparkles, Trash2, UserRoundCheck, X } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  EMPLOYEE_PERSONAL_SECTIONS,
  EMPLOYEE_PERSONAL_FORM_CASE_LABELS,
  EMPLOYEE_PERSONAL_FORM_CASE_OPTIONS,
  EMPLOYEE_PERSONAL_FORM_CASE_ORDER,
  EMPLOYEE_PERSONAL_NAME_PART_OPTIONS,
  EMPLOYEE_PERSONAL_SECTION_CHECK_LABELS,
  EMPLOYEE_PERSONAL_SECTION_LABELS,
  employeePersonalFieldsForSection,
  employeePersonalMissingRequiredFields,
  employeePersonalSectionHasFieldChanges,
  getEmployeePersonalEmergencyContacts,
  getEmployeePersonalValue,
  setEmployeePersonalEmergencyContacts,
  setEmployeePersonalValue,
  type EmployeePersonalEmergencyContact,
  type EmployeePersonalFieldSpec,
  type EmployeePersonalSection,
  type EmployeePersonalSectionDecision,
} from '@shared/types';
import { Header } from '@/components/Header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/auth';
import { cn } from '@/lib/utils';
import {
  employeePersonalDataApi,
  type EmployeePersonalAttention,
  type EmployeePersonalAssistWarning,
  type EmployeePersonalNameFormsSuggestion,
  type EmployeePersonalPayload,
  type EmployeePersonalPositionFormsSuggestion,
  type EmployeePersonalSnapshot,
  type EmployeePersonalSnapshotAuditLog,
} from '@/api/employee-personal-data';

function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(value: string): string {
  const normalized = value.length === 7 ? `${value}-01` : value;
  return new Intl.DateTimeFormat('ru-RU', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${normalized}T00:00:00.000Z`));
}

function dateLabel(value?: string | null): string {
  if (!value) return 'не отправлено';
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function gateReasonLabel(reason?: string | null): string {
  switch (reason) {
    case 'OK':
      return 'Сверка пройдена';
    case 'SNAPSHOT_REQUIRED':
      return 'Нужно заполнить сверку';
    case 'REVIEW_REQUIRED':
      return 'Нужно подтвердить секции';
    case 'ATTENTION_PENDING':
      return 'Ожидает обработки администратором';
    default:
      return 'Проверяется';
  }
}

function auditActionLabel(action: EmployeePersonalSnapshotAuditLog['action']): string {
  if (action === 'CREATE') return 'Создание';
  if (action === 'UPDATE') return 'Изменение';
  if (action === 'DELETE') return 'Удаление';
  return action;
}

function auditDiffLabel(diff?: EmployeePersonalSnapshotAuditLog['diff']): string {
  const keys = Object.keys(diff ?? {});
  if (keys.length === 0) return 'Изменения без детального diff';
  const visible = keys.slice(0, 5).join(', ');
  return keys.length > 5 ? `${visible}, еще ${keys.length - 5}` : visible;
}

function emptyDecisions(): Record<EmployeePersonalSection, boolean> {
  return EMPLOYEE_PERSONAL_SECTIONS.reduce((acc, section) => {
    acc[section] = false;
    return acc;
  }, {} as Record<EmployeePersonalSection, boolean>);
}

function decisionsFromSnapshot(snapshot?: EmployeePersonalSnapshot | null): Record<EmployeePersonalSection, boolean> {
  const next = emptyDecisions();
  if (!snapshot) return next;
  for (const section of EMPLOYEE_PERSONAL_SECTIONS) {
    next[section] = snapshot.sectionState?.[section]?.decision === 'UNCHANGED';
  }
  return next;
}

function sectionDecisions(
  confirmed: Record<EmployeePersonalSection, boolean>,
): Partial<Record<EmployeePersonalSection, EmployeePersonalSectionDecision>> {
  return EMPLOYEE_PERSONAL_SECTIONS.reduce((acc, section) => {
    acc[section] = confirmed[section] ? 'UNCHANGED' : 'NEEDS_ATTENTION';
    return acc;
  }, {} as Partial<Record<EmployeePersonalSection, EmployeePersonalSectionDecision>>);
}

function confirmedSections(confirmed: Record<EmployeePersonalSection, boolean>): EmployeePersonalSection[] {
  return EMPLOYEE_PERSONAL_SECTIONS.filter((section) => confirmed[section]);
}

function requiredFieldsToast(missingFields: EmployeePersonalFieldSpec[]) {
  if (missingFields.length === 0) return true;
  const labels = missingFields.slice(0, 4).map((field) => `${EMPLOYEE_PERSONAL_SECTION_LABELS[field.section]}: ${field.label}`);
  const suffix = missingFields.length > labels.length ? ` и еще ${missingFields.length - labels.length}` : '';
  toast.error(`Заполните обязательные поля: ${labels.join(', ')}${suffix}`);
  return false;
}

interface SelfReviewPreSubmitWarning {
  key: string;
  section?: EmployeePersonalSection;
  message: string;
}

function selfReviewPreSubmitWarnings(
  payload: EmployeePersonalPayload,
  snapshot: EmployeePersonalSnapshot,
  confirmed: Record<EmployeePersonalSection, boolean>,
): SelfReviewPreSubmitWarning[] {
  const warnings: SelfReviewPreSubmitWarning[] = [];

  for (const section of EMPLOYEE_PERSONAL_SECTIONS) {
    if (confirmed[section]) continue;
    const hasSavedDiff = snapshot.diffSections?.includes(section) ?? false;
    const hasUnsavedDiff = employeePersonalSectionHasFieldChanges(payload, snapshot.payload ?? {}, section);
    if (!hasSavedDiff && !hasUnsavedDiff) {
      warnings.push({
        key: `declared-without-change-${section}`,
        section,
        message: `${EMPLOYEE_PERSONAL_SECTION_LABELS[section]}: заявлено изменение, но данные в секции не изменены.`,
      });
    }
  }

  if (getEmployeePersonalEmergencyContacts(payload).length === 0) {
    warnings.push({
      key: 'missing-emergency-contact',
      section: 'contacts',
      message: 'Добавьте хотя бы один контакт на крайний случай.',
    });
  }

  return warnings;
}

function attentionLabel(item: EmployeePersonalAttention): string {
  return item.source === 'SECTION_DIFF'
    ? 'отличается от прошлого месяца'
    : 'сотрудник снял галочку';
}

const FORM_CASE_LABELS = EMPLOYEE_PERSONAL_FORM_CASE_LABELS;
const FORM_CASE_ORDER = EMPLOYEE_PERSONAL_FORM_CASE_ORDER;
const FORM_CASE_KEYS = EMPLOYEE_PERSONAL_FORM_CASE_OPTIONS.map((item) => item.key);
const NAME_FORM_PARTS = EMPLOYEE_PERSONAL_NAME_PART_OPTIONS;

function formCaseLabel(key: string): string {
  return (FORM_CASE_LABELS as Record<string, string>)[key] ?? key;
}

interface AssistFieldSuggestion {
  path: string;
  label: string;
  value: string;
}

type AssistSection = 'identity' | 'employment';

interface AssistPanelState {
  section: AssistSection;
  title: string;
  reason: string;
  warnings: EmployeePersonalAssistWarning[];
  fields: AssistFieldSuggestion[];
}

interface AcceptedAssistSuggestion {
  source: 'AI_SUGGESTED';
  section: AssistSection;
  title: string;
  path: string;
  label: string;
  value: string;
  acceptedAt: string;
}

function appendAcceptedAssistSuggestions(
  metadata: Record<string, unknown>,
  entries: AcceptedAssistSuggestion[],
): Record<string, unknown> {
  const existing = Array.isArray(metadata.acceptedSuggestions)
    ? metadata.acceptedSuggestions.filter((item): item is AcceptedAssistSuggestion => Boolean(item))
    : [];
  return {
    ...metadata,
    acceptedSuggestions: [...existing, ...entries].slice(-100),
  };
}

function nameAssistFields(suggested: EmployeePersonalNameFormsSuggestion): AssistFieldSuggestion[] {
  return [
    { path: 'identity.lastName', label: 'Фамилия', value: suggested.lastName },
    { path: 'identity.firstName', label: 'Имя', value: suggested.firstName },
    { path: 'identity.middleName', label: 'Отчество', value: suggested.middleName },
    ...Object.entries(suggested.forms).flatMap(([key, value]) => [
      {
        path: `identity.nameForms.${key}.lastName`,
        label: `ФИО: ${formCaseLabel(key)}, фамилия`,
        value: value.lastName,
      },
      {
        path: `identity.nameForms.${key}.firstName`,
        label: `ФИО: ${formCaseLabel(key)}, имя`,
        value: value.firstName,
      },
      {
        path: `identity.nameForms.${key}.middleName`,
        label: `ФИО: ${formCaseLabel(key)}, отчество`,
        value: value.middleName,
      },
    ]),
  ];
}

function positionAssistFields(suggested: EmployeePersonalPositionFormsSuggestion): AssistFieldSuggestion[] {
  return [
    { path: 'employment.position', label: 'Должность', value: suggested.position },
    ...Object.entries(suggested.forms).map(([key, value]) => ({
      path: `employment.positionForms.${key}`,
      label: `Должность: ${formCaseLabel(key)}`,
      value,
    })),
  ];
}

function assistWarningsForSection(
  warnings: EmployeePersonalAssistWarning[],
  section: AssistSection,
): EmployeePersonalAssistWarning[] {
  return warnings.filter((warning) => {
    const field = warning.field.toLowerCase();
    if (section === 'identity') {
      return field.startsWith('identity.')
        || field.startsWith('name.')
        || ['lastname', 'firstname', 'middlename', 'fullname'].some((key) => field.includes(key));
    }
    return field.startsWith('employment.')
      || field.startsWith('position')
      || field.includes('position');
  });
}

function applyAssistFields(payload: EmployeePersonalPayload, fields: AssistFieldSuggestion[]): EmployeePersonalPayload {
  return fields.reduce(
    (next, field) => setEmployeePersonalValue(next, field.path, field.value),
    payload,
  );
}

interface AssistCaseGroup {
  key: string;
  label: string;
  positionFields: AssistFieldSuggestion[];
  nameFields: Record<(typeof NAME_FORM_PARTS)[number]['key'], AssistFieldSuggestion | null>;
  fields: AssistFieldSuggestion[];
}

function assistCaseGroups(panel: AssistPanelState): AssistCaseGroup[] {
  const fieldsByPath = new Map(panel.fields.map((field) => [field.path, field]));
  const usedPaths = new Set<string>();
  const groups = FORM_CASE_ORDER.map((caseKey) => {
    const positionPaths = panel.section === 'employment'
      ? [caseKey === 'base' ? 'employment.position' : `employment.positionForms.${caseKey}`]
      : [];
    const namePaths = panel.section === 'identity'
      ? NAME_FORM_PARTS.map((part) => ({
          part: part.key,
          path: caseKey === 'base' ? `identity.${part.key}` : `identity.nameForms.${caseKey}.${part.key}`,
        }))
      : [];
    const positionFields = positionPaths.flatMap((path) => {
      const field = fieldsByPath.get(path);
      if (!field) return [];
      usedPaths.add(path);
      return [field];
    });
    const nameFields = NAME_FORM_PARTS.reduce((acc, part) => {
      const path = namePaths.find((item) => item.part === part.key)?.path;
      const field = path ? fieldsByPath.get(path) ?? null : null;
      if (path && field) usedPaths.add(path);
      acc[part.key] = field;
      return acc;
    }, {} as AssistCaseGroup['nameFields']);
    const fields = [...positionFields, ...Object.values(nameFields).filter((field): field is AssistFieldSuggestion => Boolean(field))];
    return {
      key: caseKey,
      label: formCaseLabel(caseKey),
      positionFields,
      nameFields,
      fields,
    };
  }).filter((group) => group.fields.length > 0);

  const remaining: AssistCaseGroup[] = panel.fields
    .filter((field) => !usedPaths.has(field.path))
    .map((field) => ({
      key: field.path,
      label: field.label,
      positionFields: panel.section === 'employment' ? [field] : [],
      nameFields: { lastName: panel.section === 'identity' ? field : null, firstName: null, middleName: null },
      fields: [field],
    }));

  return [...groups, ...remaining];
}

function assistFieldValue(field?: AssistFieldSuggestion | null): string {
  return field?.value?.trim() || '—';
}

function currentAssistFieldValue(payload: EmployeePersonalPayload, field?: AssistFieldSuggestion | null): string {
  if (!field) return '—';
  return getEmployeePersonalValue(payload, field.path) || '—';
}

function assistOverwriteCount(payload: EmployeePersonalPayload, fields: AssistFieldSuggestion[]): number {
  return fields.filter((field) => {
    const current = getEmployeePersonalValue(payload, field.path);
    return Boolean(current) && current !== field.value;
  }).length;
}

function assistEmptyFields(payload: EmployeePersonalPayload, fields: AssistFieldSuggestion[]): AssistFieldSuggestion[] {
  return fields.filter((field) => !getEmployeePersonalValue(payload, field.path));
}

function assistFieldsSame(payload: EmployeePersonalPayload, fields: AssistFieldSuggestion[]): boolean {
  return fields.every((field) => getEmployeePersonalValue(payload, field.path) === field.value);
}

function FieldInput({
  payload,
  path,
  onChange,
  disabled,
  inputType,
  options,
  placeholder,
  ariaLabel,
}: {
  payload: EmployeePersonalPayload;
  path: string;
  onChange: (path: string, value: string) => void;
  disabled: boolean;
  inputType?: 'text' | 'date' | 'textarea' | 'select' | 'emergencyContacts';
  options?: { value: string; label: string }[];
  placeholder?: string;
  ariaLabel?: string;
}) {
  const value = getEmployeePersonalValue(payload, path);
  if (inputType === 'textarea') {
    return (
      <Textarea
        value={value}
        onChange={(event) => onChange(path, event.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className="min-h-20"
      />
    );
  }
  if (inputType === 'select') {
    return (
      <select
        value={value}
        onChange={(event) => onChange(path, event.target.value)}
        disabled={disabled}
        aria-label={ariaLabel}
        className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
      >
        {(options ?? []).map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }
  return (
    <Input
      value={value}
      type={inputType === 'date' ? 'date' : 'text'}
      onChange={(event) => onChange(path, event.target.value)}
      disabled={disabled}
      placeholder={placeholder}
      aria-label={ariaLabel}
    />
  );
}

function EmployeePersonalRegularFields({
  section,
  payload,
  onChange,
  disabled,
}: {
  section: EmployeePersonalSection;
  payload: EmployeePersonalPayload;
  onChange: (path: string, value: string) => void;
  disabled: boolean;
}) {
  return (
    <>
      {employeePersonalFieldsForSection(section).filter((field) => field.inputType !== 'emergencyContacts').map((field) => (
        <label key={field.path} className="block space-y-1 text-sm">
          <span className="text-muted-foreground">{field.label}</span>
          <FieldInput
            payload={payload}
            path={field.path}
            onChange={onChange}
            disabled={disabled}
            inputType={field.inputType}
            options={field.options}
            placeholder={field.placeholder}
          />
        </label>
      ))}
    </>
  );
}

function emptyEmergencyContact(): EmployeePersonalEmergencyContact {
  return { name: '', phone: '', relation: '' };
}

function EmergencyContactsEditor({
  contacts,
  onChange,
  disabled,
}: {
  contacts: EmployeePersonalEmergencyContact[];
  onChange: (contacts: EmployeePersonalEmergencyContact[]) => void;
  disabled: boolean;
}) {
  const updateContact = (
    index: number,
    field: keyof EmployeePersonalEmergencyContact,
    value: string,
  ) => {
    onChange(contacts.map((contact, currentIndex) => (
      currentIndex === index ? { ...contact, [field]: value } : contact
    )));
  };

  return (
    <div className="overflow-hidden rounded-lg border">
      <div className="flex items-center justify-between gap-2 border-b bg-muted px-3 py-2">
        <div className="text-sm font-medium">Контакты на крайний случай</div>
        {!disabled && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onChange([...contacts, emptyEmergencyContact()])}
          >
            <Plus className="h-4 w-4" />
            Добавить
          </Button>
        )}
      </div>
      {contacts.length === 0 ? (
        <div className="px-3 py-3 text-sm text-muted-foreground">Не указаны</div>
      ) : (
        <div className="divide-y">
          {contacts.map((contact, index) => (
            <div key={index} className="space-y-2 px-3 py-3">
              <label className="block space-y-1 text-sm">
                <span className="text-xs text-muted-foreground">Имя</span>
                <Input
                  value={contact.name}
                  onChange={(event) => updateContact(index, 'name', event.target.value)}
                  disabled={disabled}
                />
              </label>
              <label className="block space-y-1 text-sm">
                <span className="text-xs text-muted-foreground">Телефон</span>
                <Input
                  value={contact.phone}
                  onChange={(event) => updateContact(index, 'phone', event.target.value)}
                  disabled={disabled}
                  placeholder="+375291234567"
                />
              </label>
              <label className="block space-y-1 text-sm">
                <span className="text-xs text-muted-foreground">Кто это</span>
                <Input
                  value={contact.relation}
                  onChange={(event) => updateContact(index, 'relation', event.target.value)}
                  disabled={disabled}
                  placeholder="мама, супруг, друг"
                />
              </label>
              {!disabled && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onChange(contacts.filter((_, currentIndex) => currentIndex !== index))}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  Удалить
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmployeeContactsFields({
  payload,
  onFieldChange,
  onPayloadChange,
  disabled,
}: {
  payload: EmployeePersonalPayload;
  onFieldChange: (path: string, value: string) => void;
  onPayloadChange: (payload: EmployeePersonalPayload) => void;
  disabled: boolean;
}) {
  const contacts = getEmployeePersonalEmergencyContacts(payload, { includeEmpty: true });

  return (
    <div className="space-y-3">
      {employeePersonalFieldsForSection('contacts').filter((field) => field.inputType !== 'emergencyContacts').map((field) => (
        <label key={field.path} className="block space-y-1 text-sm">
          <span className="text-muted-foreground">{field.label}</span>
          <FieldInput
            payload={payload}
            path={field.path}
            onChange={onFieldChange}
            disabled={disabled}
            inputType={field.inputType}
            options={field.options}
            placeholder={field.placeholder}
          />
        </label>
      ))}
      <EmergencyContactsEditor
        contacts={contacts}
        disabled={disabled}
        onChange={(nextContacts) => onPayloadChange(setEmployeePersonalEmergencyContacts(payload, nextContacts))}
      />
    </div>
  );
}

function RequiredFieldsWarning({ fields }: { fields: EmployeePersonalFieldSpec[] }) {
  if (fields.length === 0) return null;
  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
      <div className="font-medium">Заполните обязательные поля</div>
      <div className="mt-1 flex flex-wrap gap-1">
        {fields.map((field) => (
          <Badge key={field.path} variant="outline" className="border-destructive/30 bg-background text-destructive">
            {field.label}
          </Badge>
        ))}
      </div>
    </div>
  );
}

function PreSubmitWarningsCard({ warnings }: { warnings: SelfReviewPreSubmitWarning[] }) {
  if (warnings.length === 0) return null;
  return (
    <Card className="border-amber-200 bg-amber-50">
      <CardContent className="space-y-2 p-3 text-amber-900">
        <div className="flex items-start gap-2 text-sm font-medium">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          Предупреждения перед сохранением
        </div>
        <div className="space-y-1 text-sm">
          {warnings.map((warning) => (
            <div key={warning.key}>{warning.message}</div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function EmployeeIdentityFields({
  payload,
  onChange,
  disabled,
}: {
  payload: EmployeePersonalPayload;
  onChange: (path: string, value: string) => void;
  disabled: boolean;
}) {
  return (
    <>
      {[
        { path: 'identity.lastName', label: 'Фамилия' },
        { path: 'identity.firstName', label: 'Имя' },
        { path: 'identity.middleName', label: 'Отчество' },
        { path: 'identity.birthDate', label: 'Дата рождения', inputType: 'date' as const },
      ].map((field) => (
        <label key={field.path} className="block space-y-1 text-sm">
          <span className="text-muted-foreground">{field.label}</span>
          <FieldInput
            payload={payload}
            path={field.path}
            onChange={onChange}
            disabled={disabled}
            inputType={field.inputType}
          />
        </label>
      ))}
      <div className="overflow-hidden rounded-lg border">
        <div className="border-b bg-muted px-3 py-2 text-xs font-medium uppercase text-muted-foreground">
          Падежи ФИО для документов
        </div>
        <div className="divide-y">
          {FORM_CASE_KEYS.map((caseKey) => (
            <div key={caseKey} className="space-y-2 px-3 py-3">
              <div className="text-sm font-medium">{formCaseLabel(caseKey)}</div>
              <div className="grid grid-cols-1 gap-2">
                {NAME_FORM_PARTS.map((part) => (
                  <label key={part.key} className="space-y-1 text-sm">
                    <span className="text-xs text-muted-foreground">{part.label}</span>
                    <FieldInput
                      payload={payload}
                      path={`identity.nameForms.${caseKey}.${part.key}`}
                      onChange={onChange}
                      disabled={disabled}
                      placeholder={part.label}
                      ariaLabel={`ФИО ${formCaseLabel(caseKey)}: ${part.label}`}
                    />
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function EmployeeEmploymentFields({
  payload,
  onChange,
  disabled,
}: {
  payload: EmployeePersonalPayload;
  onChange: (path: string, value: string) => void;
  disabled: boolean;
}) {
  return (
    <>
      <label className="block space-y-1 text-sm">
        <span className="text-muted-foreground">Должность</span>
        <FieldInput
          payload={payload}
          path="employment.position"
          onChange={onChange}
          disabled={disabled}
        />
      </label>
      <div className="overflow-hidden rounded-lg border">
        <div className="border-b bg-muted px-3 py-2 text-xs font-medium uppercase text-muted-foreground">
          Падежи должности для документов
        </div>
        <div className="divide-y">
          {FORM_CASE_KEYS.map((caseKey) => (
            <label key={caseKey} className="block space-y-1 px-3 py-3 text-sm">
              <span className="font-medium">{formCaseLabel(caseKey)}</span>
              <FieldInput
                payload={payload}
                path={`employment.positionForms.${caseKey}`}
                onChange={onChange}
                disabled={disabled}
                placeholder="Должность в этом падеже"
                ariaLabel={`Должность ${formCaseLabel(caseKey)}`}
              />
            </label>
          ))}
        </div>
      </div>
    </>
  );
}

function SnapshotEditor({
  snapshot,
  payload,
  confirmed,
  onPayloadChange,
  onAssistAccepted,
  onConfirmedChange,
  readOnly,
  lockConfirmedSections,
  warningSections,
}: {
  snapshot?: EmployeePersonalSnapshot | null;
  payload: EmployeePersonalPayload;
  confirmed: Record<EmployeePersonalSection, boolean>;
  onPayloadChange: (payload: EmployeePersonalPayload) => void;
  onAssistAccepted?: (entries: AcceptedAssistSuggestion[]) => void;
  onConfirmedChange?: (section: EmployeePersonalSection, value: boolean) => void;
  readOnly: boolean;
  lockConfirmedSections: boolean;
  warningSections?: ReadonlySet<EmployeePersonalSection>;
}) {
  const [assistPanels, setAssistPanels] = useState<Partial<Record<AssistSection, AssistPanelState>>>({});
  const handleFieldChange = (path: string, value: string) => {
    onPayloadChange(setEmployeePersonalValue(payload, path, value));
  };

  const setAssistPanel = (section: AssistSection, panel: AssistPanelState | null) => {
    setAssistPanels((current) => {
      const next = { ...current };
      if (panel) {
        next[section] = panel;
      } else {
        delete next[section];
      }
      return next;
    });
  };

  const recordAssistAccept = (panel: AssistPanelState, fields: AssistFieldSuggestion[]) => {
    if (!onAssistAccepted) return;
    const acceptedAt = new Date().toISOString();
    onAssistAccepted(fields.map((field) => ({
      source: 'AI_SUGGESTED',
      section: panel.section,
      title: panel.title,
      path: field.path,
      label: field.label,
      value: field.value,
      acceptedAt,
    })));
  };

  const removeAcceptedAssistFields = (panel: AssistPanelState, fields: AssistFieldSuggestion[]) => {
    const acceptedPaths = new Set(fields.map((field) => field.path));
    const remainingFields = panel.fields.filter((field) => !acceptedPaths.has(field.path));
    setAssistPanel(panel.section, remainingFields.length > 0 ? { ...panel, fields: remainingFields } : null);
  };

  const acceptAssistFields = (
    panel: AssistPanelState,
    fields: AssistFieldSuggestion[],
    options: { clearPanel?: boolean } = {},
  ) => {
    if (fields.length === 0) return;
    onPayloadChange(applyAssistFields(payload, fields));
    recordAssistAccept(panel, fields);
    if (options.clearPanel) {
      setAssistPanel(panel.section, null);
    } else {
      removeAcceptedAssistFields(panel, fields);
    }
  };

  const acceptAllAssistFields = (panel: AssistPanelState) => {
    acceptAssistFields(panel, panel.fields, { clearPanel: true });
  };

  const formsAssistMutation = useMutation({
    mutationFn: () => employeePersonalDataApi.suggestForms({
      lastName: getEmployeePersonalValue(payload, 'identity.lastName'),
      firstName: getEmployeePersonalValue(payload, 'identity.firstName'),
      middleName: getEmployeePersonalValue(payload, 'identity.middleName'),
      position: getEmployeePersonalValue(payload, 'employment.position'),
    }),
    onSuccess: (result) => {
      if (!result.available || !result.suggested) {
        toast.info(result.reason || 'AI-подсказки недоступны');
        return;
      }
      const nextPanels: Partial<Record<AssistSection, AssistPanelState>> = {};
      if (result.suggested.name) {
        nextPanels.identity = {
          section: 'identity',
          title: 'Подсказка по ФИО',
          reason: result.reason,
          warnings: assistWarningsForSection(result.warnings, 'identity'),
          fields: nameAssistFields(result.suggested.name),
        };
      }
      if (result.suggested.position) {
        nextPanels.employment = {
          section: 'employment',
          title: 'Подсказка по должности',
          reason: result.reason,
          warnings: assistWarningsForSection(result.warnings, 'employment'),
          fields: positionAssistFields(result.suggested.position),
        };
      }
      if (Object.keys(nextPanels).length === 0) {
        toast.info(result.reason || 'AI-подсказки недоступны');
        return;
      }
      setAssistPanels((current) => ({ ...current, ...nextPanels }));
    },
    onError: () => toast.error('Не удалось получить подсказки'),
  });

  const spellingAssistMutation = useMutation({
    mutationFn: async (section: 'identity' | 'employment') => {
      const fields = employeePersonalFieldsForSection(section)
        .filter((field) => !['date', 'select'].includes(field.inputType ?? 'text'))
        .map((field) => ({
          fieldName: field.path,
          fieldLabel: field.label,
          value: getEmployeePersonalValue(payload, field.path),
        }))
        .filter((field) => field.value.trim().length >= 2);
      return {
        section,
        result: await employeePersonalDataApi.suggestFieldChecks({ fields }),
      };
    },
    onSuccess: ({ section, result }) => {
      if (!result.available || !result.suggested) {
        toast.info(result.reason || 'AI-проверка недоступна');
        return;
      }
      const fieldSpecs = employeePersonalFieldsForSection(section);
      const fields = Object.entries(result.suggested).flatMap(([path, check]) => {
        if (!check?.hasError || !check.suggested) return [];
        return [{
          path,
          label: fieldSpecs.find((field) => field.path === path)?.label ?? path,
          value: check.suggested,
        }];
      });
      if (fields.length === 0) {
        toast.success('Явных опечаток не найдено');
        return;
      }
      setAssistPanel(section, {
        section,
        title: 'Проверка опечаток',
        reason: result.reason,
        warnings: result.warnings,
        fields,
      });
    },
    onError: () => toast.error('Не удалось проверить опечатки'),
  });

  return (
    <div className="space-y-3">
      {EMPLOYEE_PERSONAL_SECTIONS.map((section) => {
        const checked = confirmed[section];
        const state = snapshot?.sectionState?.[section];
        const hasDiff = snapshot?.diffSections?.includes(section);
        const missingRequiredFields = employeePersonalMissingRequiredFields(payload, [section]);
        const locked = readOnly || (lockConfirmedSections && checked && missingRequiredFields.length === 0);
        const hasPreSubmitWarning = warningSections?.has(section) ?? false;
        const assistSection = section === 'identity' || section === 'employment' ? section : null;
        const assistPanel = assistSection ? assistPanels[assistSection] : null;

        return (
          <Card
            key={section}
            className={cn(
              hasDiff && 'border-amber-300',
              hasPreSubmitWarning && 'border-amber-300 bg-amber-50/40',
            )}
          >
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-base">{EMPLOYEE_PERSONAL_SECTION_LABELS[section]}</CardTitle>
                <div className="flex flex-col items-end gap-1">
                  {section === 'identity' && !locked && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => formsAssistMutation.mutate()}
                      disabled={formsAssistMutation.isPending}
                    >
                      {formsAssistMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                      Падежи
                    </Button>
                  )}
                  {(section === 'identity' || section === 'employment') && !locked && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => spellingAssistMutation.mutate(section)}
                      disabled={spellingAssistMutation.isPending}
                    >
                      {spellingAssistMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                      Опечатки
                    </Button>
                  )}
                  {section === 'employment' && !locked && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => formsAssistMutation.mutate()}
                      disabled={formsAssistMutation.isPending}
                    >
                      {formsAssistMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                      Падежи
                    </Button>
                  )}
                  {state?.decision === 'NEEDS_ATTENTION' && !state?.adminProcessed && (
                    <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
                      обработка
                    </Badge>
                  )}
                  {state?.adminProcessed && (
                    <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                      обработано
                    </Badge>
                  )}
                  {hasPreSubmitWarning && (
                    <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
                      проверьте
                    </Badge>
                  )}
                  {missingRequiredFields.length > 0 && (
                    <Badge variant="outline" className="border-destructive/30 bg-destructive/10 text-destructive">
                      обязательные: {missingRequiredFields.length}
                    </Badge>
                  )}
                </div>
              </div>
              {onConfirmedChange && (
                <label className="mt-2 flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(value) => onConfirmedChange(section, Boolean(value))}
                    disabled={readOnly}
                  />
                  <span>{EMPLOYEE_PERSONAL_SECTION_CHECK_LABELS[section]}</span>
                </label>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              <RequiredFieldsWarning fields={missingRequiredFields} />
              {assistPanel && (
                <div className="rounded-lg border border-primary/25 bg-primary/5 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium">{assistPanel.title}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{assistPanel.reason}</div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setAssistPanel(assistPanel.section, null)}
                      className="h-8 w-8"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  {assistPanel.warnings.length > 0 && (
                    <div className="mt-2 space-y-1 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                      {assistPanel.warnings.map((warning, index) => (
                        <div key={`${warning.field}:${index}`}>
                          {warning.message}
                          {warning.suggested ? `: ${warning.suggested}` : ''}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-2 overflow-hidden rounded-md border bg-background">
                    {assistCaseGroups(assistPanel).map((group) => {
                      const overwriteCount = assistOverwriteCount(payload, group.fields);
                      const same = assistFieldsSame(payload, group.fields);
                      return (
                        <div
                          key={group.key}
                          className={cn(
                            'space-y-2 border-b px-3 py-2 last:border-b-0',
                            overwriteCount > 0 && 'bg-amber-50',
                          )}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="text-sm font-medium">{group.label}</div>
                            {overwriteCount > 0 && (
                              <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
                                заменит {overwriteCount}
                              </Badge>
                            )}
                          </div>
                          {assistPanel.section === 'identity' ? (
                            <div className="grid grid-cols-1 gap-2">
                              {NAME_FORM_PARTS.map((part) => {
                                const field = group.nameFields[part.key];
                                return (
                                  <div key={part.key} className="rounded bg-background/70 px-2 py-1">
                                    <div className="text-[11px] font-medium uppercase text-muted-foreground">{part.label}</div>
                                    <div className="break-words text-sm font-medium">{assistFieldValue(field)}</div>
                                    <div className="break-words text-xs text-muted-foreground">
                                      сейчас: {currentAssistFieldValue(payload, field)}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="rounded bg-background/70 px-2 py-1">
                              <div className="break-words text-sm font-medium">{assistFieldValue(group.positionFields[0])}</div>
                              <div className="break-words text-xs text-muted-foreground">
                                сейчас: {currentAssistFieldValue(payload, group.positionFields[0])}
                              </div>
                            </div>
                          )}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={same}
                            onClick={() => acceptAssistFields(assistPanel, group.fields)}
                          >
                            {same ? 'ОК' : 'Принять'}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => acceptAssistFields(assistPanel, assistEmptyFields(payload, assistPanel.fields))}
                      disabled={assistEmptyFields(payload, assistPanel.fields).length === 0}
                    >
                      Пустые
                    </Button>
                    <Button
                      type="button"
                      onClick={() => acceptAllAssistFields(assistPanel)}
                    >
                      <Sparkles className="h-4 w-4" />
                      Все
                    </Button>
                  </div>
                </div>
              )}
              {section === 'identity' ? (
                <EmployeeIdentityFields payload={payload} onChange={handleFieldChange} disabled={locked} />
              ) : section === 'employment' ? (
                <EmployeeEmploymentFields payload={payload} onChange={handleFieldChange} disabled={locked} />
              ) : section === 'contacts' ? (
                <EmployeeContactsFields
                  payload={payload}
                  onFieldChange={handleFieldChange}
                  onPayloadChange={onPayloadChange}
                  disabled={locked}
                />
              ) : (
                <EmployeePersonalRegularFields
                  section={section}
                  payload={payload}
                  onChange={handleFieldChange}
                  disabled={locked}
                />
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function SelfReview({
  month,
  workflowKey,
  returnTo,
}: {
  month: string;
  workflowKey: string;
  returnTo: string | null;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [payload, setPayload] = useState<EmployeePersonalPayload>({});
  const [assistMetadata, setAssistMetadata] = useState<Record<string, unknown>>({});
  const [confirmed, setConfirmed] = useState<Record<EmployeePersonalSection, boolean>>(emptyDecisions);
  const [preSubmitWarnings, setPreSubmitWarnings] = useState<SelfReviewPreSubmitWarning[]>([]);

  const snapshotQuery = useQuery({
    queryKey: ['mobile-employee-personal-data', 'self', month],
    queryFn: () => employeePersonalDataApi.getSelfSnapshot(month),
  });
  const gateQuery = useQuery({
    queryKey: ['mobile-employee-personal-data', 'gate', month, workflowKey],
    queryFn: () => employeePersonalDataApi.getGate(month, workflowKey),
  });

  const snapshot = snapshotQuery.data?.snapshot;

  useEffect(() => {
    if (!snapshot) return;
    setPayload(snapshot.payload ?? {});
    setAssistMetadata(snapshot.assistMetadata ?? {});
    setConfirmed(decisionsFromSnapshot(snapshot));
    setPreSubmitWarnings([]);
  }, [snapshot?.id, snapshot?.payloadHash]);

  const saveMutation = useMutation({
    mutationFn: () => employeePersonalDataApi.saveSelfSnapshot({ period: month, payload, assistMetadata }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mobile-employee-personal-data'] });
      toast.success('Черновик сохранен');
    },
    onError: () => toast.error('Не удалось сохранить'),
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      const saved = await employeePersonalDataApi.saveSelfSnapshot({ period: month, payload, assistMetadata });
      return employeePersonalDataApi.submitSelfReview({
        period: month,
        snapshotId: saved.item.id,
        sectionDecisions: sectionDecisions(confirmed),
        workflowKey,
      });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['mobile-employee-personal-data'] });
      if (result.item.attentionSections.length > 0) {
        toast.warning('Сохранено, есть секции для обработки');
      } else {
        toast.success('Сверка сохранена');
      }
      if (result.item.allowedAfterSave && returnTo) {
        navigate(returnTo, { replace: true });
      }
    },
    onError: () => toast.error('Не удалось отправить сверку'),
  });

  const clearPreSubmitWarnings = () => {
    setPreSubmitWarnings((current) => (current.length > 0 ? [] : current));
  };

  const updatePayload = (nextPayload: EmployeePersonalPayload) => {
    clearPreSubmitWarnings();
    setPayload(nextPayload);
  };

  const updateConfirmedSection = (section: EmployeePersonalSection, value: boolean) => {
    clearPreSubmitWarnings();
    setConfirmed((current) => ({ ...current, [section]: value }));
  };

  const collectPreSubmitWarnings = () => {
    if (!snapshot) return [];
    const warnings = selfReviewPreSubmitWarnings(payload, snapshot, confirmed);
    setPreSubmitWarnings(warnings);
    return warnings;
  };

  const notifyPreSubmitWarnings = (warnings: SelfReviewPreSubmitWarning[]) => {
    if (warnings.length > 0) {
      toast.warning('Есть предупреждения перед сохранением. Запись не заблокирована.');
    }
  };

  const saveDraft = () => {
    const warnings = collectPreSubmitWarnings();
    notifyPreSubmitWarnings(warnings);
    saveMutation.mutate();
  };

  const submitReview = () => {
    const warnings = collectPreSubmitWarnings();
    const missingFields = employeePersonalMissingRequiredFields(payload, confirmedSections(confirmed));
    if (!requiredFieldsToast(missingFields)) return;
    notifyPreSubmitWarnings(warnings);
    submitMutation.mutate();
  };

  if (snapshotQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Загрузка
      </div>
    );
  }

  if (!snapshot) {
    return <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">Не удалось загрузить данные</div>;
  }

  const gate = gateQuery.data;
  const missingSections = gate?.missingSections ?? [];
  const attentionSections = gate?.attentionSections ?? [];
  const gateMode = workflowKey !== 'default' || Boolean(returnTo);
  const preSubmitWarningSections = new Set<EmployeePersonalSection>(
    preSubmitWarnings
      .map((warning) => warning.section)
      .filter((section): section is EmployeePersonalSection => Boolean(section)),
  );

  return (
    <div className="space-y-3">
      {gateMode && (
        <Card className={gate?.allowed ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}>
          <CardContent className="space-y-2 p-3">
            <div className="flex items-start gap-2">
              {gate?.allowed ? (
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
              ) : (
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
              )}
              <div>
                <div className="font-medium">
                  {gate?.allowed ? 'Сверка для продолжения готова' : 'Нужна сверка перед продолжением'}
                </div>
                <div className="mt-1 text-sm">
                  {gateReasonLabel(gate?.reason)}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  После отправки сверки система вернет вас в предыдущий раздел.
                </div>
              </div>
            </div>
            {(missingSections.length > 0 || attentionSections.length > 0) && (
              <div className="flex flex-wrap gap-1">
                {missingSections.map((section) => (
                  <Badge key={`missing-${section}`} variant="outline" className="bg-background">
                    подтвердить: {EMPLOYEE_PERSONAL_SECTION_LABELS[section]}
                  </Badge>
                ))}
                {attentionSections.map((section) => (
                  <Badge key={`attention-${section}`} variant="outline" className="border-amber-300 bg-background text-amber-700">
                    обработка: {EMPLOYEE_PERSONAL_SECTION_LABELS[section]}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
      <Card>
        <CardContent className="space-y-3 p-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-xs text-muted-foreground">Месяц</div>
              <div className="font-semibold capitalize">{monthLabel(month)}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-muted-foreground">Отправлено</div>
              <div className="text-sm">{dateLabel(snapshot.submittedAt)}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              onClick={saveDraft}
              disabled={saveMutation.isPending || submitMutation.isPending}
            >
              {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Черновик
            </Button>
            <Button
              onClick={submitReview}
              disabled={saveMutation.isPending || submitMutation.isPending}
            >
              {submitMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserRoundCheck className="h-4 w-4" />}
              Сверка
            </Button>
          </div>
        </CardContent>
      </Card>

      <PreSubmitWarningsCard warnings={preSubmitWarnings} />

      <SnapshotEditor
        snapshot={snapshot}
        payload={payload}
        confirmed={confirmed}
        onPayloadChange={updatePayload}
        onAssistAccepted={(entries) => setAssistMetadata((current) => appendAcceptedAssistSuggestions(current, entries))}
        onConfirmedChange={updateConfirmedSection}
        readOnly={false}
        lockConfirmedSections
        warningSections={preSubmitWarningSections}
      />

      <Card>
        <CardContent className="grid grid-cols-2 gap-2 p-3">
          <Button
            variant="outline"
            onClick={saveDraft}
            disabled={saveMutation.isPending || submitMutation.isPending}
          >
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Черновик
          </Button>
          <Button
            onClick={() => submitMutation.mutate()}
            disabled={saveMutation.isPending || submitMutation.isPending}
          >
            {submitMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserRoundCheck className="h-4 w-4" />}
            Сверка
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function SnapshotHistory({ snapshotId }: { snapshotId: string }) {
  const historyQuery = useQuery({
    queryKey: ['mobile-employee-personal-data', 'snapshot-history', snapshotId],
    queryFn: () => employeePersonalDataApi.getSnapshotHistory(snapshotId),
  });
  const items = historyQuery.data?.items ?? [];

  return (
    <div className="rounded-lg border border-border p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-sm font-medium">История изменений</div>
        {historyQuery.isFetching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>
      {historyQuery.isLoading ? (
        <div className="py-2 text-sm text-muted-foreground">Загрузка истории</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-muted-foreground">История пока пустая</div>
      ) : (
        <div className="max-h-64 space-y-2 overflow-auto">
          {items.map((item) => (
            <div key={item.id} className="rounded-md bg-muted p-2 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium">{auditActionLabel(item.action)}</span>
                <span className="text-xs text-muted-foreground">{dateLabel(item.createdAt)}</span>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                {item.userName || item.userId || 'Система'}
              </div>
              <div className="mt-1 break-words text-xs text-muted-foreground">
                {auditDiffLabel(item.diff)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Control({ month }: { month: string }) {
  const queryClient = useQueryClient();
  const { hasAnyRole } = useAuth();
  const [selected, setSelected] = useState<EmployeePersonalAttention | null>(null);
  const [payload, setPayload] = useState<EmployeePersonalPayload>({});
  const [assistMetadata, setAssistMetadata] = useState<Record<string, unknown>>({});
  const [comment, setComment] = useState('');
  const canProcess = hasAnyRole(['admin', 'employee_personal_data_admin']);

  const attentionQuery = useQuery({
    queryKey: ['mobile-employee-personal-data', 'attention', month],
    queryFn: () => employeePersonalDataApi.listAttention({ period: month }),
  });
  const snapshotQuery = useQuery({
    queryKey: ['mobile-employee-personal-data', 'snapshot', selected?.snapshotId],
    queryFn: () => employeePersonalDataApi.getSnapshot(selected!.snapshotId),
    enabled: Boolean(selected?.snapshotId),
  });

  useEffect(() => {
    if (!snapshotQuery.data) return;
    setPayload(snapshotQuery.data.payload ?? {});
    setAssistMetadata(snapshotQuery.data.assistMetadata ?? {});
  }, [snapshotQuery.data?.id, snapshotQuery.data?.payloadHash]);

  const updateMutation = useMutation({
    mutationFn: () => employeePersonalDataApi.updateSnapshot(selected!.snapshotId, payload, assistMetadata),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mobile-employee-personal-data'] });
      toast.success('Snapshot сохранен');
    },
    onError: () => toast.error('Не удалось сохранить snapshot'),
  });

  const processMutation = useMutation({
    mutationFn: () => employeePersonalDataApi.processSection(selected!.snapshotId, selected!.section, comment.trim() || undefined),
    onSuccess: () => {
      setComment('');
      setSelected(null);
      queryClient.invalidateQueries({ queryKey: ['mobile-employee-personal-data'] });
      toast.success('Секция обработана');
    },
    onError: () => toast.error('Не удалось обработать секцию'),
  });

  const items = attentionQuery.data?.items ?? [];
  const snapshot = snapshotQuery.data;

  return (
    <div className="space-y-3">
      {attentionQuery.isLoading ? (
        <div className="flex items-center justify-center py-10 text-muted-foreground">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Загрузка
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-4 text-center text-sm text-muted-foreground">
            Нет секций, требующих обработки
          </CardContent>
        </Card>
      ) : (
        items.map((item) => (
          <button
            key={`${item.snapshotId}:${item.section}`}
            type="button"
            onClick={() => setSelected(item)}
            className={cn(
              'w-full rounded-lg border bg-card p-3 text-left',
              selected?.snapshotId === item.snapshotId && selected.section === item.section
                ? 'border-primary'
                : 'border-border',
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-medium">{item.employeeName}</div>
                <div className="text-sm text-muted-foreground">
                  {EMPLOYEE_PERSONAL_SECTION_LABELS[item.section]} · {monthLabel(item.period)}
                </div>
                <div className="mt-1 text-xs text-amber-700">{attentionLabel(item)}</div>
              </div>
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
          </button>
        ))
      )}

      {selected && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{selected.employeeName}</CardTitle>
            <div className="text-sm text-muted-foreground">
              {EMPLOYEE_PERSONAL_SECTION_LABELS[selected.section]} · {attentionLabel(selected)}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {snapshotQuery.isLoading || !snapshot ? (
              <div className="flex items-center justify-center py-6 text-muted-foreground">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Загрузка snapshot
              </div>
            ) : (
              <>
                <SnapshotEditor
                  snapshot={snapshot}
                  payload={payload}
                  confirmed={decisionsFromSnapshot(snapshot)}
                  onPayloadChange={setPayload}
                  onAssistAccepted={(entries) => setAssistMetadata((current) => appendAcceptedAssistSuggestions(current, entries))}
                  readOnly={!canProcess}
                  lockConfirmedSections={false}
                />
                {canProcess && (
                  <div className="space-y-2">
                    <Textarea
                      value={comment}
                      onChange={(event) => setComment(event.target.value)}
                      placeholder="Комментарий администратора данных"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant="outline"
                        onClick={() => updateMutation.mutate()}
                        disabled={updateMutation.isPending || processMutation.isPending}
                      >
                        {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Сохранить
                      </Button>
                      <Button
                        onClick={() => processMutation.mutate()}
                        disabled={updateMutation.isPending || processMutation.isPending}
                      >
                        {processMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                        Обработано
                      </Button>
                    </div>
                  </div>
                )}
                <SnapshotHistory snapshotId={snapshot.id} />
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function EmployeePersonalDataPage() {
  const { hasAnyRole } = useAuth();
  const [searchParams] = useSearchParams();
  const workflowKey = searchParams.get('workflowKey') || 'default';
  const rawReturnTo = searchParams.get('returnTo');
  const returnTo = rawReturnTo?.startsWith('/') ? rawReturnTo : null;
  const [month, setMonth] = useState(searchParams.get('period')?.slice(0, 7) || currentMonth());
  const [tab, setTab] = useState<'self' | 'control'>(
    searchParams.get('tab') === 'control' || searchParams.get('tab') === 'attention' ? 'control' : 'self',
  );
  const canUseControl = hasAnyRole(['admin', 'employee_personal_data_admin', 'accountant', 'manager']);

  return (
    <div className="min-h-screen bg-background">
      <Header title="Сверка данных" showBack />
      <main className="space-y-3 p-4">
        <Input
          type="month"
          value={month}
          onChange={(event) => setMonth(event.target.value)}
        />
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant={tab === 'self' ? 'default' : 'outline'}
            onClick={() => setTab('self')}
          >
            Моя сверка
          </Button>
          {canUseControl && (
            <Button
              variant={tab === 'control' ? 'default' : 'outline'}
              onClick={() => setTab('control')}
            >
              Контроль
            </Button>
          )}
        </div>
        {tab === 'control' && canUseControl
          ? <Control month={month} />
          : <SelfReview month={month} workflowKey={workflowKey} returnTo={returnTo} />}
      </main>
    </div>
  );
}
